import { useEffect, useState, forwardRef } from "react";
import {
  IconButton,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slide,
  Paper,
} from "@mui/material";

import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import AgGridPaginatedTable from "../../components/AgGridPaginatedTable.jsx";

import {
  deleteMeeting,
  getUserMeetings,
  getUserZoomMeetings,
} from "../../api/meetings.js";
// column definition
import {
  zoomMeetingsTable,
  meetingsTable,
} from "../../config/grid-column-defs.js";

import { DeleteForever } from "@mui/icons-material";
import useSnackbar from "../../hooks/useSnackbar.js";
import useErrorHandler from "../../hooks/useErrorHandler.js";

// component name
export default function Meetings({ caseId }) {
  const { data: user } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [deletingMeetingId, setDeletingMeetingId] = useState("");
  const [formatProviderType, setFormatProviderType] = useState("");
  const [meetingTableRefreshCount, setMeetingTableRefreshCount] = useState(0);
  const errorHandler = useErrorHandler();
  // call the function to refresh the table
  function refreshMeetingTable() {
    setMeetingTableRefreshCount((prev) => prev + 1);
  }

  // for the column with 'delete icon'
  useEffect(() => {
    if (zoomMeetingsTable.some((m) => m.field === "action")) return;
    zoomMeetingsTable.unshift({
      field: "action",
      headerName: "Action",
      value: "DEL",
      maxWidth: 100,
      cellRenderer: () => (
        <IconButton size="small" variant="contained" color="error">
          <DeleteForever />
        </IconButton>
      ),
    });
  });

  // when a cell from the table is clicked
  function onMeetingTableCellClicked({ colDef, data }) {
    if (colDef.field === "action") {
      setDeletingMeetingId(data.id);
      console.log("formatted====", data);
      setFormatProviderType(formatMeetingProvider(data.type));
    }
  }

  // data from server
  async function getMeetingTableData(limit, offset) {
    try {
      const { data } = await getUserZoomMeetings(caseId, user.token, {
        limit,
        offset,
      });
      return {
        rows: data.data.meetings,
        total: data.data.total_records,
        error: false,
      };
    } catch (error) {
      errorHandler(error);
      return { error: true };
    } finally {
      setLoading(false);
    }
  }

  // after deletion is completed
  const onDeleteComplete = () => {
    refreshMeetingTable();
    setDeletingMeetingId("");
  };

  // actual UI as seen on the screen
  return (
    <>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Link to={`meetings/new`}>
          <Button
            variant="contained"
            size="large"
            color="primary"
            className="blue-button"
          >
            Create Meeting
          </Button>
        </Link>
        {loading ? <CircularProgress /> : <></>}
      </Box>
      <div className="odr-main-table">
        <AgGridPaginatedTable
          getTableRows={getMeetingTableData}
          onCellClicked={onMeetingTableCellClicked}
          columns={zoomMeetingsTable}
          perPage={10}
          refreshCounter={meetingTableRefreshCount}
        />
      </div>
      <DeleteMeetingDialog
        deletingMeetingId={deletingMeetingId}
        setOpen={setDeletingMeetingId}
        onDeleteComplete={onDeleteComplete}
        token={user.token}
        provider={formatProviderType}
        msTeamsToken={user.msTeamsAuth}
      />
    </>
  );
}

// for chosing the flying direction of the delete dialog box
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function DeleteMeetingDialog({
  setOpen,
  deletingMeetingId,
  onDeleteComplete,
  token,
  provider,
  msTeamsToken,
}) {
  const snackbar = useSnackbar();
  const errorHandler = useErrorHandler();
  const { data: user } = useSelector((state) => state.user);
  // when 'CANCEL' is clicked on delete dialog.
  const handleClose = () => {
    setOpen("");
  };

  // api call to the server with rowId of selected meeting for deleting meeting
  // when 'DELETE' is clicked on delete dialog.
  const handleDelete = async () => {
    try {
      await deleteMeeting(deletingMeetingId, token, provider, msTeamsToken);
      snackbar.showSuccess("Meeting Deleted.");
      onDeleteComplete();
    } catch (error) {
      errorHandler(error);
      setOpen("");
    }
  };

  // UI that flies in when clicked on delete.
  return (
    <Dialog
      open={Boolean(deletingMeetingId)}
      TransitionComponent={Transition}
      keepMounted
      //onClose={handleClose}
      onClose={() => {}}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle className="heading-title">Delete Meeting</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-slide-description">
          The meeting will be deleted forever.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          size="large"
          variant="outlined"
          onClick={handleClose}
          className="black-border-button"
        >
          Cancel
        </Button>
        <Button
          size="large"
          variant="text"
          color="error"
          className="red-boder-button"
          disabled={/^trial_user$/i.test(user.roles) ? true : false}
          onClick={handleDelete}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}
function formatMeetingProvider(value) {
  switch (value) {
    case "ZOOM-MEETING":
      return "Zoom";
    case "TEAMS-MEETING":
      return "teams";
    default:
      return "Unlisted";
  }
}
